import React, {FC} from "react";
import { PostDataType } from "data/types";
import { useAppDispatch, useAppSelector } from "app/hooks";
import PostCardLikeAction, {PostCardLikeActionProps} from "components/PostCardLikeAction/PostCardLikeAction";
import {selectRecentLikeds, selectRecentRemoveds, removeLikedByPostId, addNewLikedByPostId} from "app/postLikes/postLikes";

import {auth, db} from "configs/firebase-config";
import {doc, setDoc, updateDoc, increment, deleteDoc, serverTimestamp} from "firebase/firestore";
import {displayToast, toastType} from "../../global/Functions";

export interface PostCardLikeContainerProps
  extends Omit<PostCardLikeActionProps, "isLiked" | "likeCount"> {
  like: PostDataType["like"];
  data?: any;
}

const PostCardLikeContainer: FC<PostCardLikeContainerProps> = ({
  like,
  postId,
  data,
  onClickLike,
  ...args
}) => {
  const recentLikeds = useAppSelector(selectRecentLikeds);
  const recentRemoveds = useAppSelector(selectRecentRemoveds);
  const dispatch = useAppDispatch();
  const likeDocRef = doc(db, "post-hearts", `${auth.currentUser?.uid}_${postId}`)
  const postDocRef = doc(db, "posts", `${data?.userId}/posts/${postId}`);

  const isLiked = () => {
    if (recentLikeds.includes(postId)) {
      return true;
    }
    return like.isLiked && !recentRemoveds.includes(postId);

  };

  const getLikeCount = (): number => {
    // Recent Liked
    if (!auth.currentUser) return like.count;
    if (recentLikeds.includes(postId)) {
      if (data?.source !== "SINGLE"){
        return like.count + 1;
      }
    }
    if (like.isLiked && recentRemoveds.includes(postId)) {
      if (data?.source !== "SINGLE"){
        return like.count - 1;
      }
    }
    return like.count;
  };

  const handleClickLike = async () => {
    if (isLiked()) {
      if (auth.currentUser?.uid) {
        await deleteDoc(likeDocRef);
      } else {
        displayToast("Log In to give the author a like", toastType.info);
        return;
      }
      await updateDoc(postDocRef, {heartCount: increment(-1), priority: increment(-5)});
      dispatch(removeLikedByPostId(postId));
    } else {
      if (auth.currentUser?.uid) {
        await setDoc(likeDocRef, {postId: data.id, userId: auth.currentUser?.uid, likedAt: serverTimestamp()});
      }else {
        displayToast("Log In to give the author a like", toastType.info);
        return;
      }
      await updateDoc(postDocRef, {heartCount: increment(1), priority: increment(5)});
      dispatch(addNewLikedByPostId(postId));
    }
    onClickLike && onClickLike(postId);
  };

  return (
    <PostCardLikeAction
      {...args}
      isLiked={isLiked()}
      likeCount={getLikeCount()}
      postId={postId}
      onClickLike={async () => await handleClickLike()}
    />
  );
};

export default PostCardLikeContainer;
